import { Form } from "@unform/web";
import {
  Building,
  Files,
  History,
  Pencil,
  X,
  ClipboardPlus,
  TreePalm,
  File as FileIcon,
  Trash,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import FileInput from "~/components/RegisterForm/FileInput";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { Zoom, toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import CheckboxInput from "~/components/RegisterForm/CheckboxInput";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import {
  countries_list,
  getRegistries,
  handleUpdatedFields,
} from "~/functions";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import Textarea from "~/components/RegisterForm/Textarea";
import DatePicker from "~/components/RegisterForm/DatePicker";
import { format, parseISO } from "date-fns";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSelector } from "react-redux";
import { AlertContext } from "~/App";
import { Scope } from "@unform/core";
import FileInputMultiple from "~/components/RegisterForm/FileInputMultiple";
import uploadFile from "~/functions/uploadFile";
import PhoneNumberInput from "~/components/RegisterForm/PhoneNumberInput";
import { FullGridContext } from "../..";
import FindGeneric from "~/components/Finds/FindGeneric";
import {
  employeeSubtypeOptions,
  employeeTypeOptions,
  employmentTypeOptions,
  wageTypeOptions,
  yesOrNoOptions,
} from "~/functions/selectPopoverOptions";

export const InputContext = createContext({});

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    documents: [],
    name: "",
    last_name: "",
    email: "",
    jobdescription_id: null,
    jobdescription: null,
    department_id: null,
    birth_country: "",
    state: "",
    city: "",
    date_of_birth: null,
    is_student: false,
    is_us_citizen: false,
    whatsapp: "",
    address: "",
    comments: "",
    wage_amount: 0,
    loaded: false,
    hours_per_week: "",
    customize_per_day: false,
    from_allweek: "",
    to_allweek: "",
    use_same_hours_allweek: true,
    monday_works: false,
    tuesday_works: false,
    wednesday_works: false,
    thursday_works: false,
    friday_works: false,
    saturday_works: false,
    sunday_works: false,
    monday_start: "",
    monday_end: "",
    tuesday_start: "",
    tuesday_end: "",
    wednesday_start: "",
    wednesday_end: "",
    thursday_start: "",
    thursday_end: "",
    friday_start: "",
    friday_end: "",
    saturday_start: "",
    saturday_end: "",
    sunday_start: "",
    sunday_end: "",
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");

  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const generalForm = useRef();
  const medicalVacationForm = useRef();
  const auth = useSelector((state) => state.auth);
  const { alertBox } = useContext(AlertContext);
  const [loading, setLoading] = useState(false);
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [jobHistoryLoading, setJobHistoryLoading] = useState(false);
  const [medicalVacationData, setMedicalVacationData] = useState([]);
  const [medicalVacationLoading, setMedicalVacationLoading] = useState(false);
  const [medicalVacationSubmitting, setMedicalVacationSubmitting] =
    useState(false);
  const [vacationsBalance, setVacationsBalance] = useState(null);
  const [activePeriodTabVacations, setActivePeriodTabVacations] = useState(0);
  const [medicalBalance, setMedicalBalance] = useState(null);
  const [activePeriodTabMedical, setActivePeriodTabMedical] = useState(0);

  const getSelectedPeriodByMenu = (menu = activeMenu) => {
    const balanceData = menu === "medical_excuse" ? medicalBalance : vacationsBalance;
    const periods = balanceData?.periods || [];
    const activeIdx =
      menu === "medical_excuse" ? activePeriodTabMedical : activePeriodTabVacations;
    return periods[activeIdx] || null;
  };

  const getSelectedPeriodPayload = (menu = activeMenu) => {
    const selectedPeriod = getSelectedPeriodByMenu(menu);
    if (!selectedPeriod) {
      return {};
    }

    return {
      accrual_period_start: selectedPeriod.accrual_period_start || null,
      accrual_period_end: selectedPeriod.accrual_period_end || null,
      eligible_period_start: selectedPeriod.eligible_period_start || null,
      eligible_period_end: selectedPeriod.eligible_period_end || null,
    };
  };

  const loadSelectedPeriodBalance = async (menu = activeMenu) => {
    if (id === "new") {
      return;
    }
    const currentEndpoint = menu === "medical_excuse" ? "medical_excuse" : "vacations";
    const selectedPeriodPayload = getSelectedPeriodPayload(menu);
    if (!selectedPeriodPayload.accrual_period_start) {
      return;
    }

    const { data } = await api.get(`/staff/${currentEndpoint}/balance/${id}`, {
      params: selectedPeriodPayload,
    });

    if (menu === "medical_excuse") {
      setMedicalBalance(data);
      return;
    }

    setVacationsBalance(data);
  };

  const countriesOptions = countries_list.map((country) => {
    return { value: country, label: country };
  });

  useEffect(() => {
    async function getPageData() {
      if (id !== "new") {
        try {
          const { data } = await api.get(`/staffs/${id}`);
          const formatTimeString = (timeStr) =>
            timeStr && typeof timeStr === "string"
              ? timeStr.slice(0, 5)
              : timeStr || null;
          const timeFields = [
            "from_allweek",
            "to_allweek",
            "monday_start",
            "monday_end",
            "tuesday_start",
            "tuesday_end",
            "wednesday_start",
            "wednesday_end",
            "thursday_start",
            "thursday_end",
            "friday_start",
            "friday_end",
            "saturday_start",
            "saturday_end",
            "sunday_start",
            "sunday_end",
          ];
          const normalizedTimes = {};
          timeFields.forEach((field) => {
            normalizedTimes[field] = formatTimeString(data[field]);
          });
          const dayKeys = [
            "monday_start",
            "monday_end",
            "tuesday_start",
            "tuesday_end",
            "wednesday_start",
            "wednesday_end",
            "thursday_start",
            "thursday_end",
            "friday_start",
            "friday_end",
            "saturday_start",
            "saturday_end",
            "sunday_start",
            "sunday_end",
          ];
          const hasDayHours = dayKeys.some((key) => !!data[key]);
          const useSameFromApi = data.use_same_hours_allweek;
          const resolvedUseSame =
            typeof useSameFromApi === "boolean" ? useSameFromApi : !hasDayHours;
          const useSame = data.use_same_hours_allweek;
          const customizePerDay =
            typeof useSame === "boolean" ? !useSame : data.customize_per_day;
          setPageData({
            ...data,
            ...normalizedTimes,
            jobdescription_id:
              data.jobdescription_id || data.jobdescription?.id,
            jobdescription: data.jobdescription || null,
            department_id:
              data.department_id || data.jobdescription?.department_id || null,
            customize_per_day:
              typeof customizePerDay === "boolean"
                ? customizePerDay
                : !resolvedUseSame,
            use_same_hours_allweek: resolvedUseSame,
            loaded: true,
          });
          const {
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          } = data;
          const registries = await getRegistries({
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          });
          setRegistry(registries);
        } catch (err) {
          if (err?.response?.data?.error) {
            toast(err.response.data.error, { type: "error", autoClose: 3000 });
          }
        }
      } else {
        setPageData({ ...pageData, loaded: true });
        setFormType("full");
      }
    }
    getPageData();
  }, []);

  useEffect(() => {
    async function loadDepartments() {
      try {
        const { data } = await api.get(
          `/departments?limit=999&page=1&orderBy=name&orderASC=ASC`
        );
        setDepartments(data.rows || []);
      } catch (err) {}
    }
    loadDepartments();
  }, []);

  useEffect(() => {
    async function loadJobDescriptionsByDepartment() {
      if (!selectedDepartment) {
        setJobDescriptions([]);
        return;
      }
      try {
        const { data } = await api.get(
          `/get-jobs-department/${selectedDepartment}`
        );
        setJobDescriptions(data || []);
      } catch (err) {
        setJobDescriptions([]);
      }
    }
    loadJobDescriptionsByDepartment();
  }, [selectedDepartment]);

  useEffect(() => {
    const deptId =
      pageData.department_id || pageData.jobdescription?.department_id || null;
    if (deptId && deptId !== selectedDepartment) {
      setSelectedDepartment(deptId);
    }
  }, [pageData.jobdescription, pageData.department_id, selectedDepartment]);

  useEffect(() => {
    async function loadJobHistory() {
      if (id === "new") {
        setJobHistory([]);
        return;
      }
      setJobHistoryLoading(true);
      try {
        const { data } = await api.get(`/staffs/${id}/job-history`);
        const sorted = Array.isArray(data)
          ? [...data].sort((a, b) => {
              const aTime = a?.effective_date
                ? new Date(a.effective_date).getTime()
                : 0;
              const bTime = b?.effective_date
                ? new Date(b.effective_date).getTime()
                : 0;
              return bTime - aTime;
            })
          : [];
        setJobHistory(sorted);
      } catch (err) {
        setJobHistory([]);
      } finally {
        setJobHistoryLoading(false);
      }
    }
    loadJobHistory();
  }, [id]);

  useEffect(() => {
    const isMedicalOrVacation =
      activeMenu === "medical_excuse" || activeMenu === "vacations";
    if (!isMedicalOrVacation) {
      return;
    }
    if (id === "new") {
      setMedicalVacationData([]);
      return;
    }

    const currentEndpoint =
      activeMenu === "medical_excuse" ? "medical_excuse" : "vacations";

    async function loadMedicalVacation() {
      setMedicalVacationLoading(true);
      setMedicalVacationData([]);
      try {
        if (activeMenu === "vacations") {
          const [{ data: listData }, { data: balanceData }] = await Promise.all([
            api.get(`/staff/${currentEndpoint}/${id}`),
            api.get(`/staff/${currentEndpoint}/balance/${id}`),
          ]);
          setMedicalVacationData(parseMedicalVacationList(listData));
          setVacationsBalance(balanceData);
          const currentPeriod = getBalanceCurrentPeriod(balanceData);
          const currentIdx = (balanceData.periods || []).findIndex(
            (p) => isSameBalancePeriod(p, currentPeriod)
          );
          setActivePeriodTabVacations(currentIdx >= 0 ? currentIdx : 0);
        } else {
          const [{ data: listData }, { data: balanceData }] = await Promise.all([
            api.get(`/staff/${currentEndpoint}/${id}`),
            api.get(`/staff/${currentEndpoint}/balance/${id}`),
          ]);
          setMedicalVacationData(parseMedicalVacationList(listData));
          setMedicalBalance(balanceData);
          const currentPeriod = getBalanceCurrentPeriod(balanceData);
          const currentIdx = (balanceData.periods || []).findIndex(
            (p) => isSameBalancePeriod(p, currentPeriod)
          );
          setActivePeriodTabMedical(currentIdx >= 0 ? currentIdx : 0);
        }
      } catch (err) {
        setMedicalVacationData([]);
      } finally {
        setMedicalVacationLoading(false);
      }
    }
    loadMedicalVacation();
  }, [activeMenu, id]);

  useEffect(() => {
    if (activeMenu !== "medical_excuse") {
      return;
    }
    if (!(medicalBalance?.periods || []).length) {
      return;
    }

    loadSelectedPeriodBalance("medical_excuse").catch(() => {});
  }, [activeMenu, activePeriodTabMedical]);

  useEffect(() => {
    if (activeMenu !== "vacations") {
      return;
    }
    if (!(vacationsBalance?.periods || []).length) {
      return;
    }

    loadSelectedPeriodBalance("vacations").catch(() => {});
  }, [activeMenu, activePeriodTabVacations]);

  useEffect(() => {
    if (pageData.employee_type && pageData.employee_subtype) {
      getDocuments();
    }
    async function getDocuments() {
      const documents = await api.get(
        `/documentsByOrigin?origin=Employees&type=${pageData.employee_type}&subtype=${pageData.employee_subtype}`
      );
      setPageData({ ...pageData, documents: documents.data });
    }
  }, [pageData.employee_type, pageData.employee_subtype]);

  function normalizeWorkSchedule(payload) {
    const asBool = (val) => val === true || val === "true";
    const asTimeOrNull = (val) =>
      val === "" || typeof val === "undefined" || val === null ? null : val;

    const fromAllweekInput = asTimeOrNull(
      payload.from_allweek ?? pageData.from_allweek
    );
    const toAllweekInput = asTimeOrNull(
      payload.to_allweek ?? pageData.to_allweek
    );
    const explicitUseSame = payload.use_same_hours_allweek;
    const useSame =
      explicitUseSame === false
        ? false
        : explicitUseSame === true
        ? true
        : !asBool(payload.customize_per_day);
    const customize = !useSame;

    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const defaultStart = useSame ? fromAllweekInput : null;
    const defaultEnd = useSame ? toAllweekInput : null;

    const normalized = {
      ...payload,
      from_allweek: customize ? null : fromAllweekInput,
      to_allweek: customize ? null : toAllweekInput,
      use_same_hours_allweek: useSame,
    };
    days.forEach((day) => {
      const worksKey = `${day}_works`;
      const startKey = `${day}_start`;
      const endKey = `${day}_end`;

      const works = asBool(payload[worksKey]);
      const dayStart = asTimeOrNull(payload[startKey] ?? pageData[startKey]);
      const dayEnd = asTimeOrNull(payload[endKey] ?? pageData[endKey]);

      normalized[startKey] = works
        ? customize
          ? dayStart
          : defaultStart
        : null;
      normalized[endKey] = works ? (customize ? dayEnd : defaultEnd) : null;
    });

    delete normalized.customize_per_day;

    return normalized;
  }

  async function handleGeneralFormSubmit(data) {
    setLoading(true);
    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      setLoading(false);
      return;
    }
    const normalizedData = normalizeWorkSchedule(data);
    const payloadBase = {
      ...normalizedData,
      department_id:
        normalizedData.department_id ||
        selectedDepartment ||
        pageData.department_id ||
        null,
      jobdescription_id:
        normalizedData.jobdescription_id || pageData.jobdescription_id || null,
      date_of_birth: normalizedData.date_of_birth
        ? format(normalizedData.date_of_birth, "yyyy-MM-dd")
        : null,
    };

    if (id === "new") {
      try {
        await api.post(`/staffs`, payloadBase);
        handleOpened(null);
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        setLoading(false);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
        setLoading(false);
      }
      return;
    }

    if (id !== "new") {
      const updated = handleUpdatedFields(payloadBase, pageData);
      const objUpdated = Object.fromEntries(updated);
      const scheduleFields = [
        "from_allweek",
        "to_allweek",
        "use_same_hours_allweek",
        "department_id",
        "monday_start",
        "monday_end",
        "tuesday_start",
        "tuesday_end",
        "wednesday_start",
        "wednesday_end",
        "thursday_start",
        "thursday_end",
        "friday_start",
        "friday_end",
        "saturday_start",
        "saturday_end",
        "sunday_start",
        "sunday_end",
        "monday_works",
        "tuesday_works",
        "wednesday_works",
        "thursday_works",
        "friday_works",
        "saturday_works",
        "sunday_works",
      ];
      scheduleFields.forEach((key) => {
        objUpdated[key] = payloadBase[key] ?? null;
      });
      objUpdated.jobdescription_id = payloadBase.jobdescription_id;
      objUpdated.department_id = payloadBase.department_id;
      objUpdated.employee_type = payloadBase.employee_type;
      objUpdated.employee_subtype = payloadBase.employee_subtype;
      try {
        await api.put(`/staffs/${id}`, objUpdated);
        handleOpened(null);
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, {
            type: "error",
            autoClose: 3000,
          });
        }
      }
      setLoading(false);
    }
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function handleInactivate() {
    try {
      await api.delete(`staffs/${id}`);
      toast("Group Inactivated!", { autoClose: 1000 });
      handleOpened(null);
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    }
  }

  function handleOutsideMail() {
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "<p>Would you like to send the staff a link to continue the registration process?</p>",
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.post(`/staffs/formMail`, { crypt: id });
              toast("E-mail sent!", { autoClose: 1000 });
            } catch (err) {
              toast("Error!", { autoClose: 1000 });
              console.log(err);
            }
          },
        },
      ],
    });
  }

  // function handleAvailability(day) {
  //     if (day === 'sunday') {
  //         if (pageData.sunday_availability) {
  //             setPageData({ ...pageData, sunday_availability: false, sunday_morning: false, sunday_afternoon: false, sunday_evening: false })
  //         } else {
  //             setPageData({ ...pageData, sunday_availability: true })
  //         }
  //     } else if (day === 'monday') {
  //         if (pageData.monday_availability) {
  //             setPageData({ ...pageData, monday_availability: false, monday_morning: false, monday_afternoon: false, monday_evening: false })
  //         } else {
  //             setPageData({ ...pageData, monday_availability: true })
  //         }
  //     } else if (day === 'tuesday') {
  //         if (pageData.tuesday_availability) {
  //             setPageData({ ...pageData, tuesday_availability: false, tuesday_morning: false, tuesday_afternoon: false, tuesday_evening: false })
  //         } else {
  //             setPageData({ ...pageData, tuesday_availability: true })
  //         }
  //     } else if (day === 'wednesday') {
  //         if (pageData.wednesday_availability) {
  //             setPageData({ ...pageData, wednesday_availability: false, wednesday_morning: false, wednesday_afternoon: false, wednesday_evening: false })
  //         } else {
  //             setPageData({ ...pageData, wednesday_availability: true })
  //         }
  //     } else if (day === 'thursday') {
  //         if (pageData.thursday_availability) {
  //             setPageData({ ...pageData, thursday_availability: false, thursday_morning: false, thursday_afternoon: false, thursday_evening: false })
  //         } else {
  //             setPageData({ ...pageData, thursday_availability: true })
  //         }
  //     } else if (day === 'friday') {
  //         if (pageData.friday_availability) {
  //             setPageData({ ...pageData, friday_availability: false, friday_morning: false, friday_afternoon: false, friday_evening: false })
  //         } else {
  //             setPageData({ ...pageData, friday_availability: true })
  //         }
  //     } else if (day === 'saturday') {
  //         if (pageData.saturday_availability) {
  //             setPageData({ ...pageData, saturday_availability: false, saturday_morning: false, saturday_afternoon: false, saturday_evening: false })
  //         } else {
  //             setPageData({ ...pageData, saturday_availability: true })
  //         }
  //     }
  // }

  function handleDeleteDocument(id) {
    // const { file } = pageData.staffdocuments.find(staffdocument => staffdocument.id === id);
    alertBox({
      title: "Attention!",
      descriptionHTML: `<p>Are you sure you want to delete this file?</p>`,
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(`/staffdocuments/${id}`);
              toast("File deleted!", { autoClose: 1000 });
              setPageData({
                ...pageData,
                staffdocuments: pageData.staffdocuments.filter(
                  (staffdocument) => staffdocument.id !== id
                ),
              });
            } catch (err) {
              if (err?.response?.data?.error) {
                toast(err.response.data.error, {
                  type: "error",
                  autoClose: 3000,
                });
              }
            }
          },
        },
      ],
    });
  }

  const parseMedicalVacationList = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.rows)) return payload.rows;
    if (Array.isArray(payload.data)) return payload.data;
    return [];
  };

  const handleMedicalVacationSubmit = async (data) => {
    if (id === "new") {
      return;
    }
    const currentEndpoint =
      activeMenu === "medical_excuse" ? "medical_excuse" : "vacations";
    setMedicalVacationSubmitting(true);
    try {
      const filesArray = Array.from(data.files || []);
      const uploadedFiles = await Promise.all(
        filesArray.map((file, index) =>
          uploadFile(file, currentEndpoint).then((res) => ({
            ...res,
            key: index + 1,
          }))
        )
      );

      const { data: postResponse } = await api.post(
        `/staff/${currentEndpoint}`,
        {
          staff_id: id,
          date_from: data.date_from
            ? format(data.date_from, "yyyy-MM-dd")
            : null,
          date_to: data.date_to ? format(data.date_to, "yyyy-MM-dd") : null,
          note: data.note,
          files: uploadedFiles,
          ...getSelectedPeriodPayload(),
        }
      );

      toast(
        `${
          currentEndpoint === "medical_excuse" ? "Medical Excuse" : "Vacation"
        } added successfully`,
        {
          autoClose: 1000,
        }
      );

      medicalVacationForm.current?.reset();
      const parsedPostList = parseMedicalVacationList(postResponse);
      if (parsedPostList.length) {
        setMedicalVacationData(parsedPostList);
      } else {
        const { data: refreshed } = await api.get(
          `/staff/${currentEndpoint}/${id}`
        );
        setMedicalVacationData(parseMedicalVacationList(refreshed));
      }
      await loadSelectedPeriodBalance();
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    } finally {
      setMedicalVacationSubmitting(false);
    }
  };

  const handleDeleteMedicalVacation = (valueForDelete) => {
    const title =
      activeMenu === "medical_excuse" ? "Medical Excuse" : "Vacation";
    const currentEndpoint =
      activeMenu === "medical_excuse" ? "medical_excuse" : "vacations";
    alertBox({
      title: "Attention!",
      descriptionHTML: `Are you sure you want to delete this ${title.toLowerCase()}? <br />This operation cannot be undone.`,
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(`/staff/${currentEndpoint}/${valueForDelete}`);
              toast(`${title} deleted successfully`, { autoClose: 1000 });
              setMedicalVacationData((prev) =>
                prev.filter((item) => item.id !== valueForDelete)
              );
            } catch (err) {
              if (err?.response?.data?.error) {
                toast(err.response.data.error, {
                  type: "error",
                  autoClose: 3000,
                });
              }
            }
          },
        },
      ],
    });
  };

  const jobDescriptionOptions = useMemo(
    () =>
      jobDescriptions.map((job) => ({
        value: job.id,
        label: job.department
          ? `${job.name} - ${job.department.name}`
          : job.name,
      })),
    [jobDescriptions]
  );

  const departmentOptions = useMemo(
    () =>
      departments.map((dept) => ({
        value: dept.id,
        label: dept.name,
      })),
    [departments]
  );

  const defaultJobDescriptionOption = useMemo(() => {
    return jobDescriptionOptions.find(
      (opt) => String(opt.value) === String(pageData.jobdescription_id)
    );
  }, [jobDescriptionOptions, pageData.jobdescription_id]);

  const defaultDepartmentOption = useMemo(() => {
    return departmentOptions.find(
      (opt) => String(opt.value) === String(selectedDepartment)
    );
  }, [departmentOptions, selectedDepartment]);

  useEffect(() => {
    if (generalForm?.current && selectedDepartment) {
      generalForm.current.setFieldValue("department_id", selectedDepartment);
    }
  }, [selectedDepartment, generalForm]);

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(parseISO(dateStr), "MMM dd, yyyy");
    } catch (err) {
      return "-";
    }
  };

  const formatPeriodLabel = (start, end) => {
    try {
      return `${format(parseISO(start), "MMM yyyy")} - ${format(parseISO(end), "MMM yyyy")}`;
    } catch {
      return `${start} - ${end}`;
    }
  };

  const formatFullDateRange = (start, end) => {
    if (!start || !end) return "-";
    try {
      return `${format(parseISO(start), "MM/dd/yyyy")} - ${format(
        parseISO(end),
        "MM/dd/yyyy"
      )}`;
    } catch {
      return `${start} - ${end}`;
    }
  };

  const getBalanceCurrentPeriod = (balanceData) => ({
    accrualStart: balanceData?.current_accrual_period?.start || null,
    accrualEnd: balanceData?.current_accrual_period?.end || null,
    eligibleStart: balanceData?.current_eligible_period?.start || null,
    eligibleEnd: balanceData?.current_eligible_period?.end || null,
  });

  const isSameBalancePeriod = (period, currentPeriod) =>
    !!period &&
    !!currentPeriod &&
    period.accrual_period_start === currentPeriod.accrualStart &&
    period.accrual_period_end === currentPeriod.accrualEnd &&
    period.eligible_period_start === currentPeriod.eligibleStart &&
    period.eligible_period_end === currentPeriod.eligibleEnd;

  const formatBalancePeriodLabel = (period) => {
    if (!period) return "-";
    try {
      return `${format(parseISO(period.accrual_period_start), "yyyy")} - ${format(
        parseISO(period.accrual_period_end),
        "yyyy"
      )}`;
    } catch {
      return `${period.accrual_period_start} - ${period.accrual_period_end}`;
    }
  };

  const filterRecordsByPeriod = (records, period) => {
    if (!period) return records;
    return records.filter((rec) => {
      if (rec.accrual_period_start) {
        return (
          rec.accrual_period_start === period.accrual_period_start &&
          rec.accrual_period_end === period.accrual_period_end
        );
      }
      const d = rec.date_from || rec.date_to;
      if (!d) return false;
      const periodStart =
        period.eligible_period_start || period.accrual_period_start;
      const periodEnd = period.eligible_period_end || period.accrual_period_end;
      return d >= periodStart && d <= periodEnd;
    });
  };

  const formatCurrencyDisplay = (amount) => {
    if (amount === null || typeof amount === "undefined") {
      return "-";
    }
    const numeric = Number(amount);
    if (Number.isNaN(numeric)) {
      return "-";
    }
    return numeric.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const isFaculty = (pageData.employee_type || "").toLowerCase() === "faculty";
  const isMedicalVacation =
    activeMenu === "medical_excuse" || activeMenu === "vacations";

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
            </div>
            <div className="flex flex-1 flex-col items-left px-4 py-2 gap-1">
              <p className="border-b mb-1 pb-1">Staff Information</p>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>Name:</strong> {pageData.name}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="general"
              >
                <Building size={16} /> General
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="documents"
                disabled={id === "new"}
                messageOnDisabled="Create the staff registry to have access to documents."
              >
                <Files size={16} /> Documents
              </RegisterFormMenu>
              {id !== "new" && (
                <>
                  <RegisterFormMenu
                    setActiveMenu={setActiveMenu}
                    activeMenu={activeMenu}
                    name="job_history"
                  >
                    <History size={16} /> Job History
                  </RegisterFormMenu>
                  <RegisterFormMenu
                    setActiveMenu={setActiveMenu}
                    activeMenu={activeMenu}
                    name="medical_excuse"
                  >
                    <ClipboardPlus size={16} /> Medical Excuse
                  </RegisterFormMenu>
                  <RegisterFormMenu
                    setActiveMenu={setActiveMenu}
                    activeMenu={activeMenu}
                    name="vacations"
                  >
                    <TreePalm size={16} /> Vacations
                  </RegisterFormMenu>
                </>
              )}
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                {isMedicalVacation &&
                  (pageData.loaded ? (
                    activeMenu === "medical_excuse" ? (
                      (() => {
                        const balanceData = medicalBalance;
                        const periods = balanceData?.periods || [];
                        const currentPeriod = getBalanceCurrentPeriod(balanceData);
                        const hasPeriods = periods.length > 0;
                        const activePeriodIdx = activePeriodTabMedical;
                        const setActivePeriodIdx = setActivePeriodTabMedical;
                        const activePeriod = hasPeriods ? (periods[activePeriodIdx] || null) : null;
                        const periodRecords = hasPeriods
                          ? filterRecordsByPeriod(medicalVacationData, activePeriod)
                          : medicalVacationData;
                        return (
                          <Form
                            ref={medicalVacationForm}
                            onSubmit={handleMedicalVacationSubmit}
                            className="w-full"
                          >
                            <InputContext.Provider
                              value={{
                                id,
                                generalForm: medicalVacationForm,
                                setSuccessfullyUpdated,
                                fullscreen,
                                setFullscreen,
                                successfullyUpdated,
                                handleCloseForm,
                                canceled: pageData.canceled_at,
                              }}
                            >
                              <FormHeader
                                loading={medicalVacationSubmitting}
                                access={access}
                                title={`Medical Excuse - ${pageData.name}`}
                                registry={registry}
                                InputContext={InputContext}
                                saveText="Save"
                                saveIcon="Save"
                                enableFullScreen={false}
                              />
                              <InputLineGroup title="MEDICAL EXCUSE" activeMenu>
                                {periods.length > 0 && (
                                  <div className="flex flex-row flex-wrap gap-1 px-4 pt-3 pb-1">
                                    {periods.map((period, idx) => {
                                      const isCurrent = isSameBalancePeriod(
                                        period,
                                        currentPeriod
                                      );
                                      const isActive = activePeriodIdx === idx;
                                      return (
                                        <button
                                          key={`period-medical-${idx}`}
                                          type="button"
                                          onClick={() => setActivePeriodIdx(idx)}
                                          className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                                            isActive
                                              ? "bg-mila_orange text-white border-mila_orange"
                                              : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                                          }`}
                                        >
                                          {formatBalancePeriodLabel(period)}
                                          {isCurrent && (
                                            <span className="ml-1 font-bold">★</span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                                {activePeriod && (
                                  <div className="px-4 py-3 bg-gray-50 border-b text-xs">
                                    <div className="flex flex-row flex-wrap gap-6">
                                      <div>
                                        <span className="text-gray-500">Accrual: </span>
                                        <strong>
                                          {formatFullDateRange(
                                            activePeriod.accrual_period_start,
                                            activePeriod.accrual_period_end
                                          )}
                                        </strong>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Eligible: </span>
                                        <strong>
                                          {formatFullDateRange(
                                            activePeriod.eligible_period_start,
                                            activePeriod.eligible_period_end
                                          )}
                                        </strong>
                                      </div>
                                    </div>
                                    <div className="flex flex-row flex-wrap gap-6 mt-2">
                                      <div>
                                        <span className="text-gray-500">Allowed: </span>
                                        <strong>
                                          {activePeriod.medical_balance !== undefined
                                            ? activePeriod.medical_balance +
                                              (activePeriod.medical_used || 0)
                                            : balanceData?.medical?.allowed ?? "-"}
                                        </strong>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Used: </span>
                                        <strong>{activePeriod.medical_used ?? "-"}</strong>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Balance: </span>
                                        <strong>{activePeriod.medical_balance ?? "-"}</strong>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <InputLine title="Add Medical Excuse">
                                  <DatePicker
                                    name="date_from"
                                    grow
                                    required
                                    title="Date From"
                                    placeholderText="MM/DD/YYYY"
                                    InputContext={InputContext}
                                  />
                                  <DatePicker
                                    name="date_to"
                                    grow
                                    required
                                    title="Date To"
                                    placeholderText="MM/DD/YYYY"
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    name="note"
                                    grow
                                    title="Note"
                                    placeholder="Enter a note"
                                    InputContext={InputContext}
                                  />
                                  <FileInputMultiple
                                    type="file"
                                    name="files"
                                    title="Multiple Files"
                                    grow
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                                <InputLine title="Records">
                                  {medicalVacationLoading ? (
                                    <div className="text-xs text-gray-500">
                                      Loading records...
                                    </div>
                                  ) : periodRecords.length === 0 ? (
                                    <div className="text-xs text-gray-500">
                                      No records found.
                                    </div>
                                  ) : (
                                    <div className="relative w-full overflow-y-scroll bg-secondary-50 rounded-xl">
                                      <table className="w-full table-auto text-xs text-left whitespace-nowrap">
                                        <thead className="sticky top-0 bg-secondary-50">
                                          <tr>
                                            <th className="p-2" style={{ width: "60px" }}></th>
                                            <th className="p-2 min-w-[120px]" style={{ width: "120px" }}>Date From</th>
                                            <th className="p-2 min-w-[120px]" style={{ width: "120px" }}>Date To</th>
                                            <th className="p-2">Note</th>
                                            <th className="p-2 text-left" style={{ width: "200px" }}>File</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {periodRecords.map((res) => (
                                            <tr key={res.id}>
                                              <td className="px-2 py-2 bg-transparent">
                                                <button
                                                  type="button"
                                                  onClick={() => handleDeleteMedicalVacation(res.id)}
                                                  className="bg-white border rounded-sm p-2 hover:bg-red-600 hover:text-white flex items-center gap-1"
                                                >
                                                  <Trash size={16} />
                                                </button>
                                              </td>
                                              <td className="px-2 py-2 bg-transparent">
                                                {res.date_from ? format(parseISO(res.date_from), "MM/dd/yyyy") : "-"}
                                              </td>
                                              <td className="px-2 py-2 bg-transparent">
                                                {res.date_to ? format(parseISO(res.date_to), "MM/dd/yyyy") : "-"}
                                              </td>
                                              <td className="px-2 py-2 bg-transparent">{res.note || "-"}</td>
                                              <td className="pl-2 py-2 bg-transparent flex flex-wrap gap-2">
                                                {(res.files || []).map((file, idx) => {
                                                  const url = file?.file?.url || file?.url;
                                                  if (!url) return null;
                                                  return (
                                                    <a
                                                      key={`${res.id}-file-${idx}`}
                                                      href={url}
                                                      target="_blank"
                                                      rel="noreferrer"
                                                      className="flex flex-row items-center gap-2 bg-white border rounded-sm p-1"
                                                    >
                                                      <FileIcon size={16} /> File {idx + 1}
                                                    </a>
                                                  );
                                                })}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </InputLine>
                              </InputLineGroup>
                            </InputContext.Provider>
                          </Form>
                        );
                      })()
                    ) : (
                      (() => {
                        const balanceData = vacationsBalance;
                        const periods = balanceData?.periods || [];
                        const currentPeriod = getBalanceCurrentPeriod(balanceData);
                        const hasPeriods = periods.length > 0;
                        const activePeriodIdx = activePeriodTabVacations;
                        const setActivePeriodIdx = setActivePeriodTabVacations;
                        const activePeriod = hasPeriods ? (periods[activePeriodIdx] || null) : null;
                        const periodRecords = hasPeriods
                          ? filterRecordsByPeriod(medicalVacationData, activePeriod)
                          : medicalVacationData;
                        return (
                          <Form
                            ref={medicalVacationForm}
                            onSubmit={handleMedicalVacationSubmit}
                            className="w-full"
                          >
                            <InputContext.Provider
                              value={{
                                id,
                                generalForm: medicalVacationForm,
                                setSuccessfullyUpdated,
                                fullscreen,
                                setFullscreen,
                                successfullyUpdated,
                                handleCloseForm,
                                canceled: pageData.canceled_at,
                              }}
                            >
                              <FormHeader
                                loading={medicalVacationSubmitting}
                                access={access}
                                title={`Vacations - ${pageData.name}`}
                                registry={registry}
                                InputContext={InputContext}
                                saveText="Save"
                                saveIcon="Save"
                                enableFullScreen={false}
                              />
                              <InputLineGroup title="VACATIONS" activeMenu>
                                {periods.length > 0 && (
                                  <div className="flex flex-row flex-wrap gap-1 px-4 pt-3 pb-1">
                                    {periods.map((period, idx) => {
                                      const isCurrent = isSameBalancePeriod(
                                        period,
                                        currentPeriod
                                      );
                                      const isActive = activePeriodIdx === idx;
                                      return (
                                        <button
                                          key={`period-${idx}`}
                                          type="button"
                                          onClick={() => setActivePeriodIdx(idx)}
                                          className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                                            isActive
                                              ? "bg-mila_orange text-white border-mila_orange"
                                              : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                                          }`}
                                        >
                                          {formatBalancePeriodLabel(period)}
                                          {isCurrent && (
                                            <span className="ml-1 font-bold">
                                              ★
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                                {activePeriod && (
                                  <div className="px-4 py-3 bg-gray-50 border-b text-xs">
                                    <div className="flex flex-row flex-wrap gap-6">
                                      <div>
                                        <span className="text-gray-500">Accrual: </span>
                                        <strong>
                                          {formatFullDateRange(
                                            activePeriod.accrual_period_start,
                                            activePeriod.accrual_period_end
                                          )}
                                        </strong>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Eligible: </span>
                                        <strong>
                                          {formatFullDateRange(
                                            activePeriod.eligible_period_start,
                                            activePeriod.eligible_period_end
                                          )}
                                        </strong>
                                      </div>
                                    </div>
                                    <div className="flex flex-row flex-wrap gap-6 mt-2">
                                      <div>
                                        <span className="text-gray-500">Allowed: </span>
                                        <strong>
                                          {activePeriod.vacation_balance !== undefined
                                            ? activePeriod.vacation_balance +
                                              (activePeriod.vacation_used || 0)
                                            : balanceData?.vacation?.allowed ?? "-"}
                                        </strong>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Used: </span>
                                        <strong>
                                          {activePeriod.vacation_used ?? "-"}
                                        </strong>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Balance: </span>
                                        <strong>
                                          {activePeriod.vacation_balance ?? "-"}
                                        </strong>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <InputLine title="Add Vacation">
                                  <DatePicker
                                    name="date_from"
                                    grow
                                    required
                                    title="Date From"
                                    placeholderText="MM/DD/YYYY"
                                    InputContext={InputContext}
                                  />
                                  <DatePicker
                                    name="date_to"
                                    grow
                                    required
                                    title="Date To"
                                    placeholderText="MM/DD/YYYY"
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    name="note"
                                    grow
                                    title="Note"
                                    placeholder="Enter a note"
                                    InputContext={InputContext}
                                  />
                                  <FileInputMultiple
                                    type="file"
                                    name="files"
                                    title="Multiple Files"
                                    grow
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                                <InputLine title="Records">
                                  {medicalVacationLoading ? (
                                    <div className="text-xs text-gray-500">
                                      Loading records...
                                    </div>
                                  ) : periodRecords.length === 0 ? (
                                    <div className="text-xs text-gray-500">
                                      No records found.
                                    </div>
                                  ) : (
                                    <div className="relative w-full overflow-y-scroll bg-secondary-50 rounded-xl">
                                      <table className="w-full table-auto text-xs text-left whitespace-nowrap">
                                        <thead className="sticky top-0 bg-secondary-50">
                                          <tr>
                                            <th
                                              className="p-2"
                                              style={{ width: "60px" }}
                                            ></th>
                                            <th
                                              className="p-2 min-w-[120px]"
                                              style={{ width: "120px" }}
                                            >
                                              Date From
                                            </th>
                                            <th
                                              className="p-2 min-w-[120px]"
                                              style={{ width: "120px" }}
                                            >
                                              Date To
                                            </th>
                                            <th className="p-2">Note</th>
                                            <th
                                              className="p-2 text-left"
                                              style={{ width: "200px" }}
                                            >
                                              File
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {periodRecords.map((res) => (
                                            <tr key={res.id}>
                                              <td className="px-2 py-2 bg-transparent">
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleDeleteMedicalVacation(
                                                      res.id
                                                    )
                                                  }
                                                  className="bg-white border rounded-sm p-2 hover:bg-red-600 hover:text-white flex items-center gap-1"
                                                >
                                                  <Trash size={16} />
                                                </button>
                                              </td>
                                              <td className="px-2 py-2 bg-transparent">
                                                {res.date_from
                                                  ? format(
                                                      parseISO(res.date_from),
                                                      "MM/dd/yyyy"
                                                    )
                                                  : "-"}
                                              </td>
                                              <td className="px-2 py-2 bg-transparent">
                                                {res.date_to
                                                  ? format(
                                                      parseISO(res.date_to),
                                                      "MM/dd/yyyy"
                                                    )
                                                  : "-"}
                                              </td>
                                              <td className="px-2 py-2 bg-transparent">
                                                {res.note || "-"}
                                              </td>
                                              <td className="pl-2 py-2 bg-transparent flex flex-wrap gap-2">
                                                {(res.files || []).map(
                                                  (file, idx) => {
                                                    const url =
                                                      file?.file?.url || file?.url;
                                                    if (!url) return null;
                                                    return (
                                                      <a
                                                        key={`${res.id}-file-${idx}`}
                                                        href={url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="flex flex-row items-center gap-2 bg-white border rounded-sm p-1"
                                                      >
                                                        <FileIcon size={16} />{" "}
                                                        File {idx + 1}
                                                      </a>
                                                    );
                                                  }
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </InputLine>
                              </InputLineGroup>
                            </InputContext.Provider>
                          </Form>
                        );
                      })()
                    )
                  ) : (
                    <FormLoading />
                  ))}
                {!isMedicalVacation && (
                  <Form
                    ref={generalForm}
                    onSubmit={handleGeneralFormSubmit}
                    className="w-full"
                  >
                    <InputContext.Provider
                      value={{
                        id,
                        generalForm,
                        setSuccessfullyUpdated,
                        fullscreen,
                        setFullscreen,
                        successfullyUpdated,
                        handleCloseForm,
                        handleInactivate,
                        handleOutsideMail,
                        canceled: pageData.canceled_at,
                      }}
                    >
                      {pageData.loaded ? (
                        <>
                          <FormHeader
                            loading={loading}
                            access={access}
                            title={pageData.name}
                            registry={registry}
                            InputContext={InputContext}
                            emailButtonText="Send registration mail to staff"
                          />
                          <InputLineGroup
                            title="GENERAL"
                            activeMenu={activeMenu === "general"}
                          >
                            <FindGeneric
                              route="filials"
                              title="Filial"
                              scope="filial"
                              required
                              InputContext={InputContext}
                              defaultValue={
                                id === "new" && auth.filial.id !== 1
                                  ? {
                                      id: auth.filial.id,
                                      name: auth.filial.name,
                                    }
                                  : {
                                      id: pageData.filial?.id,
                                      name: pageData.filial?.name,
                                    }
                              }
                              fields={[
                                {
                                  title: "Campus",
                                  name: "name",
                                },
                              ]}
                            />
                            <InputLine title="General Data">
                              <Input
                                type="text"
                                name="name"
                                required
                                grow
                                title="Name"
                                defaultValue={pageData.name}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="middle_name"
                                grow
                                title="Middle Name"
                                defaultValue={pageData.middle_name}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="last_name"
                                required
                                grow
                                title="Last Name"
                                defaultValue={pageData.last_name}
                                InputContext={InputContext}
                              />
                              <SelectPopover
                                name="birth_country"
                                grow
                                title="Nationality"
                                required
                                options={countriesOptions}
                                isSearchable
                                defaultValue={countriesOptions.find(
                                  (country) =>
                                    country.value === pageData.birth_country
                                )}
                                InputContext={InputContext}
                              />
                              <DatePicker
                                name="date_of_birth"
                                grow
                                title="Birthday"
                                required
                                defaultValue={
                                  pageData.date_of_birth
                                    ? parseISO(pageData.date_of_birth)
                                    : null
                                }
                                placeholderText="MM/DD/YYYY"
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <SelectPopover
                                name="is_student"
                                grow
                                title="Student"
                                options={yesOrNoOptions}
                                isSearchable
                                defaultValue={yesOrNoOptions.find(
                                  (type) => type.value === pageData.is_student
                                )}
                                InputContext={InputContext}
                              />
                              <SelectPopover
                                name="is_us_citizen"
                                grow
                                title="US Citizen"
                                options={yesOrNoOptions}
                                isSearchable
                                defaultValue={yesOrNoOptions.find(
                                  (type) =>
                                    type.value === pageData.is_us_citizen
                                )}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine title="Contact">
                              <Input
                                type="text"
                                name="email"
                                required
                                title="E-mail"
                                grow
                                defaultValue={pageData.email}
                                InputContext={InputContext}
                              />
                              <PhoneNumberInput
                                type="text"
                                grow
                                name="whatsapp"
                                title="Whatsapp"
                                value={pageData.whatsapp}
                                InputContext={InputContext}
                              />
                              <PhoneNumberInput
                                type="text"
                                grow
                                name="phone"
                                title="Phone Number"
                                value={pageData.phone}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine title="Location">
                              <Textarea
                                type="text"
                                name="address"
                                title="Address"
                                rows={5}
                                grow
                                defaultValue={pageData.address}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <Input
                                type="text"
                                name="state"
                                grow
                                title="State"
                                defaultValue={pageData.state}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="city"
                                grow
                                title="City"
                                defaultValue={pageData.city}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="zip"
                                grow
                                title="Zip Code"
                                defaultValue={pageData.zip}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine title="Academic">
                              <Input
                                type="text"
                                name="academic_formation"
                                grow
                                title="Academic Graduation"
                                defaultValue={pageData.academic_formation}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <SelectPopover
                                name="employee_type"
                                grow
                                required
                                title="Group Member"
                                options={employeeTypeOptions}
                                isSearchable
                                defaultValue={employeeTypeOptions.find(
                                  (data) =>
                                    data.value === pageData.employee_type
                                )}
                                setReturnPopover={(opt) => {
                                  const newType = opt?.value || null;
                                  setPageData({
                                    ...pageData,
                                    employee_type: newType,
                                  });
                                  generalForm?.current?.setFieldValue(
                                    "employee_type",
                                    newType
                                  );
                                }}
                                InputContext={InputContext}
                              />
                              <SelectPopover
                                name="employee_subtype"
                                grow
                                required
                                title="Employee Subtype"
                                options={employeeSubtypeOptions}
                                isSearchable
                                defaultValue={employeeSubtypeOptions.find(
                                  (data) =>
                                    data.value === pageData.employee_subtype
                                )}
                                InputContext={InputContext}
                              />
                              <SelectPopover
                                name="employment_type"
                                grow
                                title="Employment Type"
                                options={employmentTypeOptions}
                                isSearchable
                                defaultValue={employmentTypeOptions.find(
                                  (data) =>
                                    data.value === pageData.employment_type
                                )}
                                InputContext={InputContext}
                              />
                              <DatePicker
                                name="admission_date"
                                grow
                                required
                                title="Admission Date"
                                defaultValue={
                                  pageData.admission_date
                                    ? parseISO(pageData.admission_date)
                                    : null
                                }
                                placeholderText="MM/DD/YYYY"
                                InputContext={InputContext}
                              />
                              <DatePicker
                                name="resignation_date"
                                grow
                                title="Resignation Date"
                                defaultValue={
                                  pageData.resignation_date
                                    ? parseISO(pageData.resignation_date)
                                    : null
                                }
                                placeholderText="MM/DD/YYYY"
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine title="Job Description">
                              <SelectPopover
                                key={`department-${
                                  selectedDepartment || "none"
                                }`}
                                name="department_id"
                                grow
                                title="Department"
                                isSearchable
                                options={departmentOptions}
                                defaultValue={defaultDepartmentOption}
                                value={defaultDepartmentOption}
                                InputContext={InputContext}
                                isClearable
                                setReturnPopover={(opt) => {
                                  const deptId = opt?.value || null;
                                  setSelectedDepartment(deptId);
                                  setPageData({
                                    ...pageData,
                                    department_id: deptId,
                                    jobdescription_id: null,
                                    jobdescription: null,
                                  });
                                  generalForm?.current?.setFieldValue(
                                    "jobdescription_id",
                                    null
                                  );
                                  generalForm?.current?.setFieldValue(
                                    "wage_type",
                                    null
                                  );
                                  generalForm?.current?.setFieldValue(
                                    "wage_amount",
                                    null
                                  );
                                }}
                              />
                              {selectedDepartment ? (
                                <>
                                  <SelectPopover
                                    key={`jobdescription-${
                                      pageData.jobdescription_id || "new"
                                    }`}
                                    name="jobdescription_id"
                                    grow
                                    title="Job Description"
                                    isSearchable
                                    options={jobDescriptionOptions}
                                    defaultValue={defaultJobDescriptionOption}
                                    value={defaultJobDescriptionOption}
                                    InputContext={InputContext}
                                    isClearable
                                  />
                                  <SelectPopover
                                    name="wage_type"
                                    grow
                                    title="Wage Type"
                                    options={wageTypeOptions}
                                    isSearchable
                                    defaultValue={wageTypeOptions.find(
                                      (data) =>
                                        data.value === pageData.wage_type
                                    )}
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="text"
                                    onlyFloat
                                    name="wage_amount"
                                    grow
                                    title="Wage Amount"
                                    defaultValue={pageData.wage_amount}
                                    InputContext={InputContext}
                                  />
                                </>
                              ) : (
                                <div className="text-xs text-gray-500">
                                  Select a department to choose a job and wage.
                                </div>
                              )}
                            </InputLine>
                            {!isFaculty && (
                              <>
                                <InputLine title="Work Schedule">
                                  <Input
                                    type="number"
                                    step="0.5"
                                    name="hours_per_week"
                                    grow
                                    required
                                    title="Hours per Week"
                                    defaultValue={pageData.hours_per_week}
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                                <InputLine>
                                  <div className="flex flex-wrap items-center gap-3 w-full">
                                    {pageData.use_same_hours_allweek ? (
                                      <>
                                        <Input
                                          type="time"
                                          name="from_allweek"
                                          title="From (applies to all checked days)"
                                          grow
                                          defaultValue={
                                            pageData.from_allweek || ""
                                          }
                                          onChange={(value) =>
                                            setPageData({
                                              ...pageData,
                                              from_allweek: value || null,
                                            })
                                          }
                                          InputContext={InputContext}
                                        />
                                        <Input
                                          type="time"
                                          name="to_allweek"
                                          title="To (applies to all checked days)"
                                          grow
                                          defaultValue={
                                            pageData.to_allweek || ""
                                          }
                                          onChange={(value) =>
                                            setPageData({
                                              ...pageData,
                                              to_allweek: value || null,
                                            })
                                          }
                                          InputContext={InputContext}
                                        />
                                      </>
                                    ) : null}
                                    <CheckboxInput
                                      name="use_same_hours_allweek"
                                      title="Use same hours all week"
                                      defaultValue={
                                        pageData.use_same_hours_allweek
                                      }
                                      onClick={() => {
                                        const newUseSame =
                                          !pageData.use_same_hours_allweek;
                                        const updated = {
                                          ...pageData,
                                          use_same_hours_allweek: newUseSame,
                                          customize_per_day: !newUseSame,
                                        };
                                        if (
                                          !newUseSame &&
                                          pageData.from_allweek &&
                                          pageData.to_allweek
                                        ) {
                                          updated.monday_start =
                                            pageData.monday_works
                                              ? pageData.from_allweek
                                              : pageData.monday_start;
                                          updated.monday_end =
                                            pageData.monday_works
                                              ? pageData.to_allweek
                                              : pageData.monday_end;
                                          updated.tuesday_start =
                                            pageData.tuesday_works
                                              ? pageData.from_allweek
                                              : pageData.tuesday_start;
                                          updated.tuesday_end =
                                            pageData.tuesday_works
                                              ? pageData.to_allweek
                                              : pageData.tuesday_end;
                                          updated.wednesday_start =
                                            pageData.wednesday_works
                                              ? pageData.from_allweek
                                              : pageData.wednesday_start;
                                          updated.wednesday_end =
                                            pageData.wednesday_works
                                              ? pageData.to_allweek
                                              : pageData.wednesday_end;
                                          updated.thursday_start =
                                            pageData.thursday_works
                                              ? pageData.from_allweek
                                              : pageData.thursday_start;
                                          updated.thursday_end =
                                            pageData.thursday_works
                                              ? pageData.to_allweek
                                              : pageData.thursday_end;
                                          updated.friday_start =
                                            pageData.friday_works
                                              ? pageData.from_allweek
                                              : pageData.friday_start;
                                          updated.friday_end =
                                            pageData.friday_works
                                              ? pageData.to_allweek
                                              : pageData.friday_end;
                                          updated.saturday_start =
                                            pageData.saturday_works
                                              ? pageData.from_allweek
                                              : pageData.saturday_start;
                                          updated.saturday_end =
                                            pageData.saturday_works
                                              ? pageData.to_allweek
                                              : pageData.saturday_end;
                                          updated.sunday_start =
                                            pageData.sunday_works
                                              ? pageData.from_allweek
                                              : pageData.sunday_start;
                                          updated.sunday_end =
                                            pageData.sunday_works
                                              ? pageData.to_allweek
                                              : pageData.sunday_end;
                                        }
                                        if (!newUseSame) {
                                          generalForm?.current?.setFieldValue(
                                            "from_allweek",
                                            null
                                          );
                                          generalForm?.current?.setFieldValue(
                                            "to_allweek",
                                            null
                                          );
                                        }
                                        setPageData(updated);
                                      }}
                                      InputContext={InputContext}
                                    />
                                  </div>
                                </InputLine>
                                <InputLine>
                                  <div className="flex flex-wrap gap-6 w-full">
                                    <div className="flex flex-col gap-2">
                                      <CheckboxInput
                                        name="monday_works"
                                        title="Monday"
                                        defaultValue={pageData.monday_works}
                                        onClick={() =>
                                          setPageData({
                                            ...pageData,
                                            monday_works:
                                              !pageData.monday_works,
                                          })
                                        }
                                        InputContext={InputContext}
                                      />
                                      {!pageData.use_same_hours_allweek &&
                                        pageData.monday_works && (
                                          <div className="flex flex-col gap-1 ml-6">
                                            <Input
                                              type="time"
                                              name="monday_start"
                                              title="From"
                                              defaultValue={
                                                pageData.monday_start || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  monday_start: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                            <Input
                                              type="time"
                                              name="monday_end"
                                              title="To"
                                              defaultValue={
                                                pageData.monday_end || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  monday_end: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                          </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-2">
                                      <CheckboxInput
                                        name="tuesday_works"
                                        title="Tuesday"
                                        defaultValue={pageData.tuesday_works}
                                        onClick={() =>
                                          setPageData({
                                            ...pageData,
                                            tuesday_works:
                                              !pageData.tuesday_works,
                                          })
                                        }
                                        InputContext={InputContext}
                                      />
                                      {!pageData.use_same_hours_allweek &&
                                        pageData.tuesday_works && (
                                          <div className="flex flex-col gap-1 ml-6">
                                            <Input
                                              type="time"
                                              name="tuesday_start"
                                              title="From"
                                              defaultValue={
                                                pageData.tuesday_start || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  tuesday_start: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                            <Input
                                              type="time"
                                              name="tuesday_end"
                                              title="To"
                                              defaultValue={
                                                pageData.tuesday_end || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  tuesday_end: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                          </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-2">
                                      <CheckboxInput
                                        name="wednesday_works"
                                        title="Wednesday"
                                        defaultValue={pageData.wednesday_works}
                                        onClick={() =>
                                          setPageData({
                                            ...pageData,
                                            wednesday_works:
                                              !pageData.wednesday_works,
                                          })
                                        }
                                        InputContext={InputContext}
                                      />
                                      {!pageData.use_same_hours_allweek &&
                                        pageData.wednesday_works && (
                                          <div className="flex flex-col gap-1 ml-6">
                                            <Input
                                              type="time"
                                              name="wednesday_start"
                                              title="From"
                                              defaultValue={
                                                pageData.wednesday_start || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  wednesday_start:
                                                    value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                            <Input
                                              type="time"
                                              name="wednesday_end"
                                              title="To"
                                              defaultValue={
                                                pageData.wednesday_end || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  wednesday_end: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                          </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-2">
                                      <CheckboxInput
                                        name="thursday_works"
                                        title="Thursday"
                                        defaultValue={pageData.thursday_works}
                                        onClick={() =>
                                          setPageData({
                                            ...pageData,
                                            thursday_works:
                                              !pageData.thursday_works,
                                          })
                                        }
                                        InputContext={InputContext}
                                      />
                                      {!pageData.use_same_hours_allweek &&
                                        pageData.thursday_works && (
                                          <div className="flex flex-col gap-1 ml-6">
                                            <Input
                                              type="time"
                                              name="thursday_start"
                                              title="From"
                                              defaultValue={
                                                pageData.thursday_start || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  thursday_start: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                            <Input
                                              type="time"
                                              name="thursday_end"
                                              title="To"
                                              defaultValue={
                                                pageData.thursday_end || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  thursday_end: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                          </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-2">
                                      <CheckboxInput
                                        name="friday_works"
                                        title="Friday"
                                        defaultValue={pageData.friday_works}
                                        onClick={() =>
                                          setPageData({
                                            ...pageData,
                                            friday_works:
                                              !pageData.friday_works,
                                          })
                                        }
                                        InputContext={InputContext}
                                      />
                                      {!pageData.use_same_hours_allweek &&
                                        pageData.friday_works && (
                                          <div className="flex flex-col gap-1 ml-6">
                                            <Input
                                              type="time"
                                              name="friday_start"
                                              title="From"
                                              defaultValue={
                                                pageData.friday_start || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  friday_start: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                            <Input
                                              type="time"
                                              name="friday_end"
                                              title="To"
                                              defaultValue={
                                                pageData.friday_end || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  friday_end: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                          </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-2">
                                      <CheckboxInput
                                        name="saturday_works"
                                        title="Saturday"
                                        defaultValue={pageData.saturday_works}
                                        onClick={() =>
                                          setPageData({
                                            ...pageData,
                                            saturday_works:
                                              !pageData.saturday_works,
                                          })
                                        }
                                        InputContext={InputContext}
                                      />
                                      {!pageData.use_same_hours_allweek &&
                                        pageData.saturday_works && (
                                          <div className="flex flex-col gap-1 ml-6">
                                            <Input
                                              type="time"
                                              name="saturday_start"
                                              title="From"
                                              defaultValue={
                                                pageData.saturday_start || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  saturday_start: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                            <Input
                                              type="time"
                                              name="saturday_end"
                                              title="To"
                                              defaultValue={
                                                pageData.saturday_end || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  saturday_end: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                          </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-2">
                                      <CheckboxInput
                                        name="sunday_works"
                                        title="Sunday"
                                        defaultValue={pageData.sunday_works}
                                        onClick={() =>
                                          setPageData({
                                            ...pageData,
                                            sunday_works:
                                              !pageData.sunday_works,
                                          })
                                        }
                                        InputContext={InputContext}
                                      />
                                      {!pageData.use_same_hours_allweek &&
                                        pageData.sunday_works && (
                                          <div className="flex flex-col gap-1 ml-6">
                                            <Input
                                              type="time"
                                              name="sunday_start"
                                              title="From"
                                              defaultValue={
                                                pageData.sunday_start || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  sunday_start: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                            <Input
                                              type="time"
                                              name="sunday_end"
                                              title="To"
                                              defaultValue={
                                                pageData.sunday_end || ""
                                              }
                                              onChange={(value) =>
                                                setPageData({
                                                  ...pageData,
                                                  sunday_end: value || null,
                                                })
                                              }
                                              InputContext={InputContext}
                                            />
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                </InputLine>
                              </>
                            )}
                            <InputLine title="Notes">
                              <Textarea
                                type="text"
                                name="comments"
                                title="Comments"
                                rows={5}
                                grow
                                defaultValue={pageData.comments}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            {/* <InputLine title='Availability'>
                                                    <div className='flex flex-col md:flex-row items-center justify-start gap-4'>
                                                        <CheckboxInput name='sunday_availability' grow title='Sunday' onClick={() => handleAvailability('sunday')} defaultValue={pageData.sunday_availability} InputContext={InputContext} />
                                                        {pageData.sunday_availability &&
                                                            <div className='flex flex-row items-start justify-start gap-1'>
                                                                <CheckboxInput name='sunday_morning' grow title='Morning' onClick={() => setPageData({ ...pageData, sunday_morning: !pageData.sunday_morning })} defaultValue={pageData.sunday_morning} InputContext={InputContext} />
                                                                <CheckboxInput name='sunday_afternoon' grow title='Afternoon' onClick={() => setPageData({ ...pageData, sunday_afternoon: !pageData.sunday_afternoon })} defaultValue={pageData.sunday_afternoon} InputContext={InputContext} />
                                                                <CheckboxInput name='sunday_evening' grow title='Evening' onClick={() => setPageData({ ...pageData, sunday_evening: !pageData.sunday_evening })} defaultValue={pageData.sunday_evening} InputContext={InputContext} />
                                                            </div>
                                                        }
                                                    </div>
                                                </InputLine>
                                                <InputLine>
                                                    <div className='flex flex-col items-center justify-start gap-4'>
                                                        <CheckboxInput name='monday_availability' grow title='Monday' onClick={() => handleAvailability('monday')} defaultValue={pageData.monday_availability} InputContext={InputContext} />
                                                        {pageData.monday_availability &&
                                                            <div className='flex flex-row items-start justify-start gap-1'>
                                                                <CheckboxInput name='monday_morning' grow title='Morning' onClick={() => setPageData({ ...pageData, monday_morning: !pageData.monday_morning })} defaultValue={pageData.monday_morning} InputContext={InputContext} />
                                                                <CheckboxInput name='monday_afternoon' grow title='Afternoon' onClick={() => setPageData({ ...pageData, monday_afternoon: !pageData.monday_afternoon })} defaultValue={pageData.monday_afternoon} InputContext={InputContext} />
                                                                <CheckboxInput name='monday_evening' grow title='Evening' onClick={() => setPageData({ ...pageData, monday_evening: !pageData.monday_evening })} defaultValue={pageData.monday_evening} InputContext={InputContext} />
                                                            </div>
                                                        }
                                                    </div>
                                                </InputLine>
                                                <InputLine>
                                                    <div className='flex flex-col items-center justify-start gap-4'>
                                                        <CheckboxInput name='tuesday_availability' grow title='Tuesday' onClick={() => handleAvailability('tuesday')} defaultValue={pageData.tuesday_availability} InputContext={InputContext} />
                                                        {pageData.tuesday_availability &&
                                                            <div className='flex flex-row items-start justify-start gap-1'>
                                                                <CheckboxInput name='tuesday_morning' grow title='Morning' onClick={() => setPageData({ ...pageData, tuesday_morning: !pageData.tuesday_morning })} defaultValue={pageData.tuesday_morning} InputContext={InputContext} />
                                                                <CheckboxInput name='tuesday_afternoon' grow title='Afternoon' onClick={() => setPageData({ ...pageData, tuesday_afternoon: !pageData.tuesday_afternoon })} defaultValue={pageData.tuesday_afternoon} InputContext={InputContext} />
                                                                <CheckboxInput name='tuesday_evening' grow title='Evening' onClick={() => setPageData({ ...pageData, tuesday_evening: !pageData.tuesday_evening })} defaultValue={pageData.tuesday_evening} InputContext={InputContext} />
                                                            </div>
                                                        }
                                                    </div>
                                                </InputLine>
                                                <InputLine>
                                                    <div className='flex flex-col items-center justify-start gap-4'>
                                                        <CheckboxInput name='wednesday_availability' grow title='Wednesday' onClick={() => handleAvailability('wednesday')} defaultValue={pageData.wednesday_availability} InputContext={InputContext} />
                                                        {pageData.wednesday_availability &&
                                                            <div className='flex flex-row items-start justify-start gap-1'>
                                                                <CheckboxInput name='wednesday_morning' grow title='Morning' onClick={() => setPageData({ ...pageData, wednesday_morning: !pageData.wednesday_morning })} defaultValue={pageData.wednesday_morning} InputContext={InputContext} />
                                                                <CheckboxInput name='wednesday_afternoon' grow title='Afternoon' onClick={() => setPageData({ ...pageData, wednesday_afternoon: !pageData.wednesday_afternoon })} defaultValue={pageData.wednesday_afternoon} InputContext={InputContext} />
                                                                <CheckboxInput name='wednesday_evening' grow title='Evening' onClick={() => setPageData({ ...pageData, wednesday_evening: !pageData.wednesday_evening })} defaultValue={pageData.wednesday_evening} InputContext={InputContext} />
                                                            </div>
                                                        }
                                                    </div>
                                                </InputLine>
                                                <InputLine>
                                                    <div className='flex flex-col items-center justify-start gap-4'>
                                                        <CheckboxInput name='thursday_availability' grow title='Thursday' onClick={() => handleAvailability('thursday')} defaultValue={pageData.thursday_availability} InputContext={InputContext} />
                                                        {pageData.thursday_availability &&
                                                            <div className='flex flex-row items-start justify-start gap-1'>
                                                                <CheckboxInput name='thursday_morning' grow title='Morning' onClick={() => setPageData({ ...pageData, thursday_morning: !pageData.thursday_morning })} defaultValue={pageData.thursday_morning} InputContext={InputContext} />
                                                                <CheckboxInput name='thursday_afternoon' grow title='Afternoon' onClick={() => setPageData({ ...pageData, thursday_afternoon: !pageData.thursday_afternoon })} defaultValue={pageData.thursday_afternoon} InputContext={InputContext} />
                                                                <CheckboxInput name='thursday_evening' grow title='Evening' onClick={() => setPageData({ ...pageData, thursday_evening: !pageData.thursday_evening })} defaultValue={pageData.thursday_evening} InputContext={InputContext} />
                                                            </div>
                                                        }
                                                    </div>
                                                </InputLine>
                                                <InputLine>
                                                    <div className='flex flex-col items-center justify-start gap-4'>
                                                        <CheckboxInput name='friday_availability' grow title='Friday' onClick={() => handleAvailability('friday')} defaultValue={pageData.friday_availability} InputContext={InputContext} />
                                                        {pageData.friday_availability &&
                                                            <div className='flex flex-row items-start justify-start gap-1'>
                                                                <CheckboxInput name='friday_morning' grow title='Morning' onClick={() => setPageData({ ...pageData, friday_morning: !pageData.friday_morning })} defaultValue={pageData.friday_morning} InputContext={InputContext} />
                                                                <CheckboxInput name='friday_afternoon' grow title='Afternoon' onClick={() => setPageData({ ...pageData, friday_afternoon: !pageData.friday_afternoon })} defaultValue={pageData.friday_afternoon} InputContext={InputContext} />
                                                                <CheckboxInput name='friday_evening' grow title='Evening' onClick={() => setPageData({ ...pageData, friday_evening: !pageData.friday_evening })} defaultValue={pageData.friday_evening} InputContext={InputContext} />
                                                            </div>
                                                        }
                                                    </div>
                                                </InputLine>
                                                <InputLine>
                                                    <div className='flex flex-col items-center justify-start gap-4'>
                                                        <CheckboxInput name='saturday_availability' grow title='Saturday' onClick={() => handleAvailability('saturday')} defaultValue={pageData.saturday_availability} InputContext={InputContext} />
                                                        {pageData.saturday_availability &&
                                                            <div className='flex flex-row items-start justify-start gap-1'>
                                                                <CheckboxInput name='saturday_morning' grow title='Morning' onClick={() => setPageData({ ...pageData, saturday_morning: !pageData.saturday_morning })} defaultValue={pageData.saturday_morning} InputContext={InputContext} />
                                                                <CheckboxInput name='saturday_afternoon' grow title='Afternoon' onClick={() => setPageData({ ...pageData, saturday_afternoon: !pageData.saturday_afternoon })} defaultValue={pageData.saturday_afternoon} InputContext={InputContext} />
                                                                <CheckboxInput name='saturday_evening' grow title='Evening' onClick={() => setPageData({ ...pageData, saturday_evening: !pageData.saturday_evening })} defaultValue={pageData.saturday_evening} InputContext={InputContext} />
                                                            </div>
                                                        }
                                                    </div>
                                                </InputLine> */}
                          </InputLineGroup>
                          <InputLineGroup
                            title="DOCUMENTS"
                            activeMenu={activeMenu === "documents"}
                          >
                            {pageData.documents &&
                              pageData.documents.length > 0 &&
                              pageData.documents.map((document, index) => {
                                return (
                                  <Scope
                                    key={index}
                                    path={`documents[${index}]`}
                                  >
                                    <Input
                                      type="hidden"
                                      name="document_id"
                                      defaultValue={document.id}
                                      InputContext={InputContext}
                                    />
                                    <InputLine title={document.title}>
                                      {!document.multiple &&
                                        pageData.staffdocuments &&
                                        pageData.staffdocuments.filter(
                                          (staffdocument) =>
                                            staffdocument.document_id ===
                                            document.id
                                        ).length === 0 && (
                                          <FileInput
                                            type="file"
                                            name="file_id"
                                            title={"File"}
                                            grow
                                            InputContext={InputContext}
                                          />
                                        )}
                                      {document.multiple && (
                                        <FileInputMultiple
                                          type="file"
                                          name="file_id"
                                          title={"Multiple Files"}
                                          grow
                                          InputContext={InputContext}
                                        />
                                      )}
                                    </InputLine>
                                    {pageData.staffdocuments &&
                                      pageData.staffdocuments.length > 0 && (
                                        <InputLine subtitle="Attached Files">
                                          <div className="flex flex-col justify-center items-start gap-4">
                                            {pageData.staffdocuments &&
                                              pageData.staffdocuments.map(
                                                (staffdocument, index) => {
                                                  if (
                                                    staffdocument.document_id ===
                                                    document.id
                                                  ) {
                                                    return (
                                                      <>
                                                        <div className="flex flex-row justify-center items-center gap-2">
                                                          <a
                                                            href={
                                                              staffdocument.file
                                                                .url
                                                            }
                                                            target="_blank"
                                                            className="text-xs"
                                                          >
                                                            <div
                                                              className="flex flex-row items-center border px-4 py-2 gap-1 rounded-md bg-gray-100 hover:border-gray-300"
                                                              key={index}
                                                            >
                                                              <Files
                                                                size={16}
                                                              />
                                                              {
                                                                staffdocument
                                                                  .file.name
                                                              }
                                                            </div>
                                                          </a>
                                                          <button
                                                            type="button"
                                                            onClick={() =>
                                                              handleDeleteDocument(
                                                                staffdocument.id
                                                              )
                                                            }
                                                            className="text-xs text-red-700 cursor-pointer flex flex-row items-center justify-start gap-1 mt-1 px-2 py-1 rounded-sm hover:bg-red-100"
                                                          >
                                                            <X size={12} />{" "}
                                                            Delete
                                                          </button>
                                                        </div>
                                                      </>
                                                    );
                                                  }
                                                }
                                              )}
                                          </div>
                                        </InputLine>
                                      )}
                                  </Scope>
                                );
                              })}
                          </InputLineGroup>
                          {id !== "new" && (
                            <InputLineGroup
                              title="JOB HISTORY"
                              activeMenu={activeMenu === "job_history"}
                            >
                              <InputLine title="Job History">
                                {jobHistoryLoading ? (
                                  <div className="text-xs text-gray-500">
                                    Loading job history...
                                  </div>
                                ) : jobHistory.length === 0 ? (
                                  <div className="text-xs text-gray-500">
                                    No job history records found for this staff
                                    member.
                                  </div>
                                ) : (
                                  <div className="w-full overflow-x-auto">
                                    <table className="min-w-full text-xs">
                                      <thead className="bg-gray-100">
                                        <tr>
                                          <th className="px-2 py-2 text-left font-semibold">
                                            Effective Date
                                          </th>
                                          <th className="px-2 py-2 text-left font-semibold">
                                            End Date
                                          </th>
                                          <th className="px-2 py-2 text-left font-semibold">
                                            Department
                                          </th>
                                          <th className="px-2 py-2 text-left font-semibold">
                                            Job Description
                                          </th>
                                          <th className="px-2 py-2 text-left font-semibold">
                                            Wage Type
                                          </th>
                                          <th className="px-2 py-2 text-left font-semibold">
                                            Wage Amount
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {jobHistory.map((history) => (
                                          <tr
                                            key={history.id}
                                            className="border-t"
                                          >
                                            <td className="px-2 py-2">
                                              {formatDateDisplay(
                                                history.effective_date
                                              )}
                                            </td>
                                            <td className="px-2 py-2">
                                              {history.end_date
                                                ? formatDateDisplay(
                                                    history.end_date
                                                  )
                                                : "Current"}
                                            </td>
                                            <td className="px-2 py-2">
                                              {history.department?.name || "-"}
                                            </td>
                                            <td className="px-2 py-2">
                                              {history.jobdescription?.name ||
                                                history.jobdescription?.title ||
                                                "-"}
                                            </td>
                                            <td className="px-2 py-2 capitalize">
                                              {history.wage_type || "-"}
                                            </td>
                                            <td className="px-2 py-2">
                                              {formatCurrencyDisplay(
                                                history.wage_amount
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </InputLine>
                            </InputLineGroup>
                          )}
                        </>
                      ) : (
                        <FormLoading />
                      )}
                    </InputContext.Provider>
                  </Form>
                )}
              </div>
            </div>
          </div>
        )
      ) : null}
    </Preview>
  );
}
